/**
 * The phone's page acts like an app, not a web page (Marek, 2026-09-30): no pinch to zoom, and the
 * page itself never left scrolled. The viewport already asks for no zoom, but iOS Safari ignores
 * that, so its pinch gestures are stopped here, and so is any move made with two fingers down.
 * Two thumbs tapping at once (the rhythm game) are untouched: only a moving pair is stopped, and
 * the games listen to pointer events, which this does not cancel. One finger scrolls as before.
 *
 * Called once by each page's entry (the guest app, the practice page, the door).
 */
export function holdStill(): void {
  if (typeof document === 'undefined') return;
  const stop = (e: Event) => e.preventDefault();
  for (const type of ['gesturestart', 'gesturechange', 'gestureend']) {
    document.addEventListener(type, stop, { passive: false });
  }
  // Only where the viewport's no to zooming is ignored (iOS). A listener like this makes a phone wait
  // for the page before every scroll, and on Android, with a game drawing, that left the page all but
  // unscrollable (Marek, 2026-09-30); Android keeps to the viewport, so it goes without.
  if (isIos()) {
    document.addEventListener(
      'touchmove',
      (e) => {
        if (e.touches.length > 1) e.preventDefault();
      },
      { passive: false },
    );
  }
  // No copy or share menu from a long press — on a word, a picture or a link — except in a box being
  // typed in (styles.css keeps words from being selected at all).
  document.addEventListener('contextmenu', (e) => {
    const at = e.target;
    if (at instanceof HTMLInputElement || at instanceof HTMLTextAreaElement) return;
    e.preventDefault();
  });
  // iOS nudges even a pinned page up to show a text box above the keyboard; once the typing is
  // over, put it back where it belongs.
  document.addEventListener('focusout', () => {
    setTimeout(() => {
      const typing = document.activeElement;
      if (typing instanceof HTMLInputElement || typing instanceof HTMLTextAreaElement) return;
      if (window.scrollX !== 0 || window.scrollY !== 0) window.scrollTo(0, 0);
    }, 60);
  });
}

/** An iPhone or iPad (an iPad says it is a Mac, but has a touch screen). */
function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}
